import { getEventStatusById, getOutfitNameById } from 'services/cfg/form'
import { getDateAndTimeAsString, getDateAsString } from 'services/utils/date'
import { getDefaultListParams } from 'services/utils'
import { EntityDescriptor } from '.'
import {
  parseCsvFields,
  parseMultilangJSON,
} from 'services/utils/multilangParseWrite'
import { getCurrentLang } from 'services/contexts/LangContext'
import { KampaayDataProvider } from 'services/api/dataProvider'
import { EventItem } from 'services/api/entities/events/types/internal/event-models'
import {
  MainProductSelection,
  OptionedProductSelection,
  DrinkSelection,
  OptionGroupSelection,
  FoodSelection,
} from 'services/api/entities/events/types/internal/selections'
import {
  EventProfessionalService,
  EventProductService,
} from 'services/api/entities/events/types/internal/services'

//-----------------------------
export const formatBaseProduct = (m: MainProductSelection, obj?: Object) => ({
  ...obj,
  ...parseCsvFields({ Name: m.name! }),
  Quantity: m.quantity,
})
const formatMediumProduct = (m: OptionedProductSelection) => {
  return {
    ...formatBaseProduct(m),
    Options: formatEventOptions(m.optionSelections ?? []),
    Upselling: m.upsellingSelection?.map((u) => u.name ?? '').join(', '),
  }
}
//-----------------------------
const BASE_DRINK = {
  Name: ' ',
  Quantity: ' ',
  'Variant Name': ' ',
  'Variant Quantity': ' ',
}
const mapDrinks = (drink: DrinkSelection) =>
  drink.variants!.map((variant, idx) => ({
    ...BASE_DRINK,
    ...parseCsvFields({
      Name: idx === 0 ? drink.name! : ' ',
      'Variant Name': variant.name ?? '',
    }),
    'Variant Quantity': variant.quantity,
  }))
const getFormattedProfessionals = (proTypes: EventProfessionalService[] = []) =>
  proTypes.reduce(
    (acc, p) => ({
      ...acc,
      [`${p.name} Customization`]: p.customization?.name,
      [`${p.name} Supplier`]: p.supplierName,
      [p.name]: p.selections.map((f) => ({
        ...formatBaseProduct(f),
        'Start Service': f.startTime,
        'End Service': f.endTime,
      })),
    }),
    {}
  )
const formatEventOptions = (groups: OptionGroupSelection[]) =>
  groups
    .flatMap((grp) =>
      grp.items.map(({ name, quantity }) => `${name} (${quantity})`)
    )
    .join(', ')
const getFormattedFoods = (foods: FoodSelection[]) =>
  foods?.map(({ deliveryTime, intolerances, logisticSelection, ...rest }) => ({
    ...formatMediumProduct(rest),
    'Delivery Time': deliveryTime,
    Intolerances: Object.entries(intolerances)
      .map(([key, value]) => `${value} ${key}`)
      .join(', '),
    Logistic: logisticSelection!.name,
  }))

const h = {
  id: 'ID',
  status: 'Status',
  startDate: 'Start Date',
  endDate: 'Event End Date',
  creationDate: 'Creation Date',
  customer: 'Client',
  phone: 'Phone',
  guests: 'Guests',
  address: 'Address',
  drinkSupplier: 'Drink Supplier',
  drinksName: 'Drinks.Name',
  drinksQuantity: 'Drinks.Quantity',
  foodsName: 'Foods.Name',
  foodsQuantity: 'Foods.Quantity',
  foodOptions: 'Foods.Options',
  foodsUpselling: 'Foods.Upselling',
  foodsDeliveryTime: 'Foods.Delivery Time',
  foodsLogistic: 'Foods.Logistic',
  foodSupplier: 'Food Supplier',
  musicDeliveryTime: 'Music Delivery Time',
  musicPickUpDate: 'Music Pick up Date',
  musicsName: 'Musics.Name',
  musicsQuantity: 'Musics.Quantity',
  musicsSupplier: 'Musics Supplier',
  rentDeliveryTime: 'Rent Delivery Time',
  rentPickUpDate: 'Rent Pick up Date',
  rentsName: 'Rents.Name',
  rentsQuantity: 'Rents.Quantity',
  rentsSupplier: 'Rents Supplier',
  virtualName: 'Virtual.Name',
  virtualSupplier: 'Virtual Supplier',
  virtualQuantity: 'Virtual.Quantity',
  virtualOptions: 'Virtual.Options',
  virtualUpselling: 'Virtual.Upselling',
  professionalOutfit: 'Professional Outfit',
  drinksCustomization: 'Drinks Customization',
  foodsCustomization: 'Foods Customization',
  musicsCustomization: 'Musics Customization',
  rentsCustomization: 'Rents Customization',
  virtualsCustomization: 'Virtuals Customization',
}
export const events: EntityDescriptor<EventItem> = {
  headers: Object.values(h),
  format:
    (dp?: KampaayDataProvider) =>
    async ({
      id,
      eventStatus,
      startDate,
      endDate,
      createdAt,
      customerInfo: { phone, name },
      guestsNumber,
      fullAddress,
      professionalOutfit,
      drinks,
      foods,
      musicDeliveryTime,
      musicPickupDate,
      virtuals,
      professionals,
      rents,
      musics,
      rentDeliveryTime,
      rentPickupDate,
      productServices: {
        foodService,
        musicService,
        rentService,
        drinkService,
        virtualService,
      },
    }: EventItem) => {
      if (!dp) throw new Error('DataProviderProxy is required')
      const professionalsTypes = await dp
        .getList('professionalsections', getDefaultListParams())
        .then((e) => e?.data)

      const parseMultilangCustomization = (
        s?: Pick<EventProductService, 'customization'>
      ) => {
        return parseMultilangJSON(s?.customization?.name)[getCurrentLang()]
      }

      const professionalLabels = professionalsTypes.flatMap(({ name }) => [
        `${name} Supplier`,
        `${name}.Name`,
        `${name}.Quantity`,
        `${name}.Start Service`,
        `${name}.End Service`,
      ])

      events.headers = [...Object.values(h), ...professionalLabels]
      const mapped = {
        [h.id]: id,
        [h.status]: getEventStatusById(eventStatus),
        [h.startDate]: getDateAsString(startDate),
        [h.endDate]: getDateAsString(endDate),
        [h.creationDate]: createdAt,
        [h.customer]: name,
        [h.phone]: phone,
        [h.guests]: guestsNumber,
        [h.address]: fullAddress,
        [h.professionalOutfit]: getOutfitNameById(professionalOutfit),
        [h.drinkSupplier]: drinkService?.supplierName,
        [h.drinksCustomization]: parseMultilangCustomization(drinkService),
        Drinks: drinks?.map(mapDrinks),
        Foods: getFormattedFoods(foods),
        [h.foodSupplier]: foodService?.supplierName,
        [h.foodsCustomization]: parseMultilangCustomization(foodService),
        [h.musicDeliveryTime]: musicDeliveryTime,
        [h.musicPickUpDate]: getDateAndTimeAsString(musicPickupDate),
        Musics: musics?.map(formatBaseProduct),
        [h.musicsSupplier]: musicService?.supplierName,
        [h.musicsCustomization]: parseMultilangCustomization(musicService),
        [h.rentDeliveryTime]: rentDeliveryTime,
        [h.rentPickUpDate]: getDateAndTimeAsString(rentPickupDate),
        Rents: rents?.map(formatBaseProduct),
        [h.rentsSupplier]: rentService?.supplierName,
        [h.rentsCustomization]: parseMultilangCustomization(rentService),
        Virtual: virtuals?.map(formatMediumProduct),
        ...getFormattedProfessionals(professionals),
        [h.virtualsCustomization]: parseMultilangCustomization(virtualService),
      }
      return mapped
    },
}
